import React, { useEffect, useMemo, useRef, useState } from "react";
import { useTranslation, withTranslation } from "react-i18next";
import PropTypes from "prop-types";
import { request } from "../../../../../utils/Request";
import { dateToUtcDate, utcDateToLocalDate } from "../../../../../utils/Helper";
import { Field, Form, Formik, FormikProps } from "formik";
import * as yup from "yup";
import { isEmpty, uniqBy } from "lodash";
import { Sidebar } from "primereact/sidebar";
import { Col, Row } from "react-bootstrap";
import { Button } from "primereact/button";
import InputDateTime from "../../../../../components/Shared/InputDateTime";
import { InputSingleSelectField } from "../../../../../components/Shared/InputSingleSelectField";
import { InputTextAreaField } from "../../../../../components/Shared/InputTextAreaField";
import GroupedLoadsByRoute from "./GroupedLoadsByRoute";
import Tooltip from "../../../../../components/Common/Display/Tooltip";
import { ConfirmPopup, confirmPopup } from "primereact/confirmpopup";
import HasAccess from "../../../../../utils/HasAccess";

interface IFormProps {
    id: string | null; // groupToken
    t: any | null;
    onRefresh?: any | null;
    dropdowns?: any | null;
    onClose?: any | null;
    toastify?: any | null;
}

type TInputForm = {
    group_token?: any;
    driver_id?: any;
    transport_vehicle_id?: any;
    estimated_loading_time?: any;
    estimated_unloading_time?: any;
    comments?: any;
};

const DEFAULT_FORM_STATE: TInputForm = {
    group_token: null,
    driver_id: null,
    transport_vehicle_id: null,
    estimated_loading_time: null,
    estimated_unloading_time: null,
    comments: null,
};

const validationSchema = (t: Function): any => {
    return yup.object().shape({
        group_token: yup.string().required(t('the field is required.', { field: t('group token') })),
        driver_id: yup.string().required(t('the field is required.', { field: t('driver') })),
        transport_vehicle_id: yup.string().required(t('the field is required.', { field: t('transport vehicle') })),
        estimated_loading_time: yup.date(t("the field value is not valid", { field: t('estimated loading time') }))
            .required(t('the field is required.', { field: t('estimated loading time') })).nullable(),
        estimated_unloading_time: yup.date(t("the field value is not valid", { field: t('estimated unloading time') }))
            .min(yup.ref('estimated_loading_time'), t('the field must be a date after', {
                field: "estimated unloading time",
                fieldAfter: "estimated loading time"
            }))
            .required(t('the field is required.', { field: t('estimated unloading time') })).nullable(),
        comments: yup.string().nullable(),
    });
};

function AlpOutboundLoadGroupAccept(props: IFormProps) {
    const { dropdowns, toastify } = props;
    const { t } = useTranslation();
    const formRef: any = useRef();
    const [entity, setEntity] = useState<any>(null);
    const [initFormState, setInitFormState] = useState<any>({ ...DEFAULT_FORM_STATE });
    const [panelState, setPanelState] = useState<boolean>(false);
    const [removing, setRemoving] = useState<boolean>(false);

    const onClose = () => {
        setPanelState(false);
        if (props.onClose) {
            props.onClose();
        }
    };

    const onSubmit = async (payloads: TInputForm, { setErrors, resetForm }: any) => {
        try {
            await request({
                method: 'POST',
                url: `/suppliers/alp/aviso/outbound/loads/${ props.id }/accept`,
                data: {
                    ...payloads,
                    estimated_unloading_time: (payloads.estimated_unloading_time
                        ? dateToUtcDate(payloads.estimated_unloading_time, 'YYYY-MM-DD[T]HH:mm:ss', 'YYYY-MM-DD HH:mm:ss')
                        : null),
                    estimated_loading_time: (payloads.estimated_loading_time
                        ? dateToUtcDate(payloads.estimated_loading_time, 'YYYY-MM-DD[T]HH:mm:ss', 'YYYY-MM-DD HH:mm:ss')
                        : null),
                },
            });

            toastify && toastify(t("record updated", { item: t('outbound load') }), "info");
            resetForm();
            onClose();

            if (props.onRefresh) {
                props.onRefresh();
            }
        } catch (e: any) {
            if (e.status === 422) {
                setErrors(e.data.errors);
            } else if (e.status === 404) {
                toastify && toastify(t("action not allowed: 404"), "error");
            } else {
                toastify && toastify(t("server error"), "error");
            }
        }
    };

    const canRemoveLoad = (load: any): boolean => {
        const status = `${ load?.status ?? '' }`.toLowerCase();
        const hasGateOut = !isEmpty(load?.gate_out_confirmed_at);
        return (status === 'announced' || status === 'accepted') && !hasGateOut;
    };

    const onRemoveLoadFromGroup = async (load: string) => {
        if ( removing || isEmpty(props.id) || isEmpty(load) ) {
            return;
        }

        const getApiErrorMessage = (e: any): string | null => {
            const errors = e?.data?.errors;
            if ( errors && typeof errors === 'object' ) {
                const messages: string[] = [];
                Object.keys(errors).forEach((key: string) => {
                    const items = (errors as any)[key];
                    if ( Array.isArray(items) ) {
                        items.forEach((m: any) => {
                            if ( m ) {
                                messages.push(String(m));
                            }
                        });
                    }
                });
                if ( messages.length > 0 ) {
                    return messages.join(', ');
                }
            }
            return e?.data?.message ? String(e.data.message) : null;
        };

        setRemoving(true);
        try {
            await request({
                method: 'POST',
                url: `/suppliers/alp/aviso/outbound/loads/${ props.id }/remove`,
                data: {
                    load_ids: [load],
                },
            });

            toastify && toastify(t("record updated", { item: t('outbound load') }), "info");
            // refresh sidebar data so the removed load disappears immediately
            // @ts-ignore
            await getDetail(props.id);
        } catch (e: any) {
            if (e.status === 422) {
                const msg = getApiErrorMessage(e);
                toastify && toastify(msg || t("unprocessable entity"), "error");
            } else if (e.status === 404) {
                toastify && toastify(t("action not allowed: 404"), "error");
            } else {
                toastify && toastify(t("server error"), "error");
            }
        } finally {
            setRemoving(false);
        }
    };

    const getDetail = async (groupToken: string) => {
        try {
            const response = await request({
                url: `/suppliers/provider/aviso/outbound/groups/${ groupToken }`,
            });
            const data = response?.data?.data ?? response?.data;
            setEntity(data);

            const firstLoad = Array.isArray(data?.alp_outbound_loads) && data.alp_outbound_loads.length > 0
                ? data.alp_outbound_loads[0]
                : null;

            setInitFormState({
                ...DEFAULT_FORM_STATE,
                group_token: groupToken,
                driver_id: firstLoad?.driver?.id ?? null,
                transport_vehicle_id: firstLoad?.transport_vehicle?.id ?? null,
                estimated_loading_time: (firstLoad?.estimated_loading_time
                    ? utcDateToLocalDate(firstLoad.estimated_loading_time, 'YYYY-MM-DD[T]HH:mm:ss', 'YYYY-MM-DD HH:mm:ss')
                    : null),
                estimated_unloading_time: (firstLoad?.estimated_unloading_time
                    ? utcDateToLocalDate(firstLoad.estimated_unloading_time, 'YYYY-MM-DD[T]HH:mm:ss', 'YYYY-MM-DD HH:mm:ss')
                    : null),
                comments: null,
            });
            setPanelState(true);
        } catch (error: any) {
            if (error.status === 404) {
                toastify && toastify(t("action not allowed: 404"), "error");
            } else {
                toastify && toastify(t("server error"), "error");
            }
        }
    };

    useEffect(() => {
        setEntity(null);
        if (!isEmpty(props.id)) {
            // @ts-ignore
            getDetail(props.id);
        }
    }, [props.id]);

    const loads = Array.isArray(entity?.alp_outbound_loads) ? entity.alp_outbound_loads : [];
    const firstLoad = loads.length > 0 ? loads[0] : null;
    const currentDriver = firstLoad?.driver ?? null;
    const currentTransportVehicle = firstLoad?.transport_vehicle ?? null;
    const driverOptions = useMemo(() => {
        const current = !isEmpty(currentDriver?.id)
            ? [{
                ...currentDriver,
                key: currentDriver.id,
                label: currentDriver.name,
                is_active: true,
            }]
            : [];

        return uniqBy([ ...current, ...(dropdowns?.drivers || []) ], (item: any) => String(item.id));
    }, [dropdowns?.drivers, currentDriver]);
    const transportVehicleOptions = useMemo(() => {
        const current = !isEmpty(currentTransportVehicle?.id)
            ? [{
                ...currentTransportVehicle,
                key: currentTransportVehicle.id,
                label: `${ currentTransportVehicle.truck_plate_number ?? '' } | ${ currentTransportVehicle.trailer_plate_number ?? '' }`,
                is_active: true,
            }]
            : [];

        return uniqBy([ ...current, ...(dropdowns?.transportVehicles || []) ], (item: any) => String(item.id))
            .filter((item: any) => item.is_active != false)
            .map((item: any) => ({
                ...item,
                label: `${ item.truck_plate_number } | ${ item.trailer_plate_number }`,
                icon: (!isEmpty(item.active_journey) ? 'fas fa-circle text-yellow-500' : 'fas fa-circle text-green-500')
            }));
    }, [dropdowns?.transportVehicles, currentTransportVehicle]);

    return (
        <>
            <div>
                <ConfirmPopup />
                <Sidebar
                    header={
                        <div className="bg-body-tertiary card-header">
                            <h5 className={ "card-title mt-0" }>{ t('accept loads') }</h5>
                            <p className="mb-0">
                                <strong>{ t("group token") }:</strong> { props.id }
                            </p>
                        </div>
                    }
                    visible={ panelState }
                    position="right"
                    onHide={ onClose }
                    blockScroll={ true }
                    style={ { width: '900px' } }
                    closeOnEscape={ false }
                >
                    <div style={ {
                        height: '3px',
                        background: 'linear-gradient(90deg, var(--primary-color) 0%, rgba(33, 150, 243, 0) 50%)'
                    } }></div>

                    <Formik
                        innerRef={ formRef }
                        enableReinitialize={ true }
                        initialValues={ initFormState }
                        onSubmit={ onSubmit }
                        validationSchema={ validationSchema(t) }
                    >
                        { (fp: FormikProps<TInputForm>) => (
                            <Form onSubmit={ fp.handleSubmit }>
                                <Row className={ "mt-4 mb-7" }>
                                    <Col sm={ 12 } md={ 6 }>
                                        <Field
                                            component={ InputSingleSelectField }
                                            name="driver_id"
                                            trackBy="id"
                                            options={ driverOptions.filter((item: any) => item.is_active != false) }
                                            label={ t('driver') }
                                            placeholder={ t('select') + ' ' + t('driver') }
                                            isRequired
                                        />
                                    </Col>
                                    <Col sm={ 12 } md={ 6 }>
                                        <Field
                                            component={ InputSingleSelectField }
                                            panelClassName={ "hide-p-toggler" }
                                            name="transport_vehicle_id"
                                            trackBy="id"
                                            options={ transportVehicleOptions }
                                            label={ t('transport vehicle') }
                                            placeholder={ t('select') + ' ' + t('transport vehicle') }
                                            isRequired
                                        />
                                    </Col>
                                    <Col sm={ 12 } md={ 6 }>
                                        <Field
                                            placeholder={ t("pick a date") }
                                            component={ InputDateTime }
                                            name={ `estimated_loading_time` }
                                            type={ "datetime" }
                                            label={ t("estimated loading time") }
                                            isRequired
                                        />
                                    </Col>
                                    <Col sm={ 12 } md={ 6 }>
                                        <Field
                                            placeholder={ t("pick a date") }
                                            component={ InputDateTime }
                                            name={ `estimated_unloading_time` }
                                            type={ "datetime" }
                                            label={ t("estimated unloading time") }
                                            isRequired
                                        />
                                    </Col>
                                    <Col sm={ 12 } md={ 12 }>
                                        <Field
                                            component={ InputTextAreaField }
                                            name="comments"
                                            label={ t('comments') }
                                            placeholder={ t("enter") + ' ' + t("comments").toLowerCase() }
                                        />
                                    </Col>

                                    <Col sm={ 12 } md={ 12 } className={ "mb-5" }>
                                        <hr />
                                        <h4 className="card-title">{ t('loads') }</h4>
                                        <GroupedLoadsByRoute
                                            loads={ loads }
                                            t={ t }
                                            // If there is only 1 load, hide delete button (user should delete the whole group).
                                            showActionColumn={ Array.isArray(loads) && loads.length > 1 }
                                            renderActionCell={ (row: any) => {
                                                if ( !Array.isArray(loads) || loads.length <= 1 ) {
                                                    return null;
                                                }
                                                const allowed = canRemoveLoad(row);
                                                const id = `${ row?.id ?? '' }`;

                                                return (
                                                    <HasAccess hasAnyAccess={ ['alpgrouploadsupdate'] }>
                                                        <Tooltip tooltip={ t('delete item') } position={ "top" }>
                                                            <a
                                                                title={ t('delete item') }
                                                                className={ `cursor-pointer ms-2 btn btn-soft-danger waves-effect waves-light btn-xs ${allowed ? '' : 'disabled'}` }
                                                                onClick={ (event: any) => {

                                                                    if ( !allowed || isEmpty(id) ) {
                                                                        return;
                                                                    }
                                                                    confirmPopup({
                                                                        target: event.currentTarget,
                                                                        message: t('do you want to delete this record?'),
                                                                        icon: 'pi pi-info-circle',
                                                                        // @ts-ignore
                                                                        defaultFocus: 'reject',
                                                                        acceptClassName: 'p-button-danger',
                                                                        accept: () => onRemoveLoadFromGroup(id),
                                                                        reject: () => {},
                                                                    });
                                                                } }
                                                            >
                                                                <i className="fas fa-trash-alt" />
                                                            </a>
                                                        </Tooltip>
                                                    </HasAccess>
                                                );
                                            } }
                                        />
                                    </Col>
                                </Row>

                                <div className="filter-card-footer" style={ { width: '885px' } }>
                                    <Button
                                        tooltip={ t("to data save into database") }
                                        size={ "small" }
                                        outlined
                                        tooltipOptions={ { position: 'top' } }
                                        disabled={ fp.isSubmitting }
                                    >
                                        <i className="pi pi-save me-2" />
                                        { t("let's save") }
                                    </Button>
                                    <Button
                                        type={ "button" }
                                        tooltip={ t("operation cancel") }
                                        className={ "ms-2" }
                                        size={ "small" }
                                        severity={ "danger" }
                                        outlined
                                        tooltipOptions={ { position: 'top' } }
                                        onClick={ onClose }
                                        disabled={ fp.isSubmitting }
                                    >
                                        { t("cancel") }
                                    </Button>
                                </div>
                            </Form>
                        ) }
                    </Formik>
                </Sidebar>
            </div>
        </>
    );
}

AlpOutboundLoadGroupAccept.propTypes = {
    t: PropTypes.any,
};

export default withTranslation()(AlpOutboundLoadGroupAccept);

